import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BreadcrumbComponent,
  MUITable,
  PaginationComponent,
  SelectInputComponent,
} from "../../../common/components";
import { routes } from "../../../constants/routes";
import moment from "moment";
import {
  getModulesWithUploadTracking,
  getUploadTrackingRecords,
  getUsersWhoUploadedFiles,
} from "../../../api/settings";
import { useSetLoading } from "../../../common/hooks/appearance";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import downloadIcon from "../../../assets/icons/download.svg";
import { Box } from "@mui/material";
import "./track-uploads.scss";
import { convertUTCToLocal } from "../../../utils/utils";
import { SortOption } from "../../tenant/requests/tenant-requests-helper";

interface UploadTrackingRecord {
  id: number;
  module_name: string;
  status: string;
  uploaded_by: {
    id: number;
    first_name: string;
    last_name: string;
  };
  created_at: string;
  error_message: string;
  inserted_rows_count: number;
  failed_rows_count: number;
  file_key: string;
  output_file_key: string;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
}
interface Filters {
  moduleName?: string;
  status?: string;
  uploadedBy?: number;
  uploadDate?: any;
}

const TrackUploads = () => {
  const [data, setData] = useState<UploadTrackingRecord[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(15);
  const [filters, setFilters] = useState<Filters>({});
  const [appliedFilters, setAppliedFilters] = useState<Filters>({});
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modules, setModules] = useState([]);
  const [sortOption, setSortOption] = useState<SortOption>({
      sortDirection: null,
      sortBy: null,
    });

  const convertToDayjs = (date?: Date): Dayjs | null => {
    if (!date) return null;
    if (Array.isArray(date)) {
      return dayjs(date[1] || null);
    }
    return dayjs(date);
  };
  const crumbMapping = [
    { label: "Settings", link: "#" },
    { label: "Track Uploads", link: routes.TRACK_UPLOADS },
  ];

  const headers = [
    { key: "serial_number", label: "S.No", isSortable: false },
    { key: "module_name", label: "Module Name", isSortable: true },
    { key: "status", label: "Status", isSortable: true },
    { key: "uploaded_by", label: "Uploaded By", isSortable: true },
    { key: "created_at", label: "Uploaded Date", isSortable: true },
    { key: "error_message", label: "Failed Reason", isSortable: true },
    {
      key: "inserted_rows_count",
      label: "Inserted Rows Count",
      isSortable: true,
    },
    { key: "failed_rows_count", label: "Failed Rows Count", isSortable: true },
    { key: "original_file", label: "Original File", isSortable: false },
    { key: "output_file", label: "Output File", isSortable: false },
  ];
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const res = await getUsersWhoUploadedFiles();
        const moduleRes = await getModulesWithUploadTracking();
        setUsers(
          res.data.data?.sort((a: User, b: User) => {
            const nameA =
              `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
            const nameB =
              `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
            return nameA.localeCompare(nameB);
          }) || []
        );
        setModules(
          moduleRes.data.data?.sort((a: string, b: string) =>
            a.localeCompare(b)
          ) || []
        );
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error("Error fetching user list:", error);
      }
    };

    fetchData();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await getUploadTrackingRecords({
        ...appliedFilters,
        page,
        limit,
        sortBy: sortOption.sortBy,
        sortDirection: sortOption.sortDirection,
      });
      setData(result.data.data.data);
      setTotalRecords(result.data.data.totalRecords);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching tracking records:", error);
      setIsLoading(false);
    }
  }, [page, limit, appliedFilters, sortOption]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const mapColumnNameToField = (columnName: string): string => {
    const mapping: Record<string, string> = {
      "Module Name": "module_name",
      Status: "status",
      "Uploaded By": "users.first_name",
      "Uploaded Date": "created_at",
      "Failed Reason": "error_message",
      "Inserted Rows Count": "inserted_rows_count",
      "Failed Rows Count": "failed_rows_count",
    };

    return mapping[columnName] || columnName;
  };
  const handleSort = (column: string, sortType: string) => {
    let sortValues: SortOption = { sortBy: null, sortDirection: null };
    if (sortType !== "Unsort") {
      const mappedField = mapColumnNameToField(column);
      sortValues = { sortBy: mappedField, sortDirection: sortType };
    }
    setPage(1);
    setSortOption(sortValues);
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);
  return (
    <div className="leadlist-wrapper track-uploads-wrapper">
      <BreadcrumbComponent breadcrumbLink={crumbMapping} />
      <div className="filter-section-tracking">
        <div className="filter-section-tracking-dropdown">
          <SelectInputComponent
            placeholder="Select a Module"
            options={modules.map((module_name) => ({
              label: module_name,
              value: module_name,
            }))}
            value={filters.moduleName || ""}
            onChange={(selectedValue: string) => {
              setFilters((prevFilters) => ({
                ...prevFilters,
                moduleName: selectedValue,
              }));
            }}
          />
        </div>
        <div className="filter-section-tracking-dropdown">
          <SelectInputComponent
            placeholder="Select a Status"
            options={[
              {
                label: "Completed",
                value: "Completed",
              },
              { label: "Failed", value: "Failed" },
              { label: "In progress", value: "In progress" },
              { label: "Pending", value: "Pending" },
            ]}
            value={filters.status || ""}
            onChange={(selectedValue: string) => {
              setFilters((prevFilters) => ({
                ...prevFilters,
                status: selectedValue,
              }));
            }}
          />
        </div>
        <div className="filter-section-tracking-dropdown">
          <SelectInputComponent
            placeholder="Select a User"
            options={users.map((user) => ({
              label: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
              value: String(user.id),
            }))}
            value={
              filters.uploadedBy
                ? `${users.find((user) => user.id === filters.uploadedBy)?.first_name || ""} ${users.find((user) => user.id === filters.uploadedBy)?.last_name || ""}`.trim()
                : ""
            }
            onChange={(selectedValue: string) => {
              setFilters((prevFilters) => ({
                ...prevFilters,
                uploadedBy: selectedValue ? parseInt(selectedValue) : undefined,
              }));
            }}
          />
        </div>
        <div className="date-picker filter-section-tracking-dropdown">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Select a Date"
              value={convertToDayjs(filters.uploadDate) as Dayjs | undefined}
              onChange={(selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  uploadDate: selected,
                }));
              }}
              maxDate={dayjs()}
            />
          </LocalizationProvider>
        </div>
        <div className="filter-buttons">
          <button
            className="button clear-button"
            onClick={() => {
              if (isFilterApplied) {
                setAppliedFilters({});
                setIsFilterApplied(false);
              }
              setFilters({});
              setSortOption({ sortBy: null, sortDirection: null });
            }}
          >
            Clear
          </button>
          <button
            className="button submit-button"
            onClick={async () => {
              if (
                !filters.status &&
                !filters.moduleName &&
                !filters.uploadedBy &&
                !filters.uploadDate
              ) {
                return;
              }
              const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
              const updatedFilters = {
                ...filters,
                timezone,
              };
              setPage(1);
              setAppliedFilters(updatedFilters);
              setIsFilterApplied(true);
            }}
          >
            Submit
          </button>
        </div>
      </div>
      <div className="lead-list-table-filter">
        <div className="lead-list-table file-tracking-table">
          <MUITable
            headers={headers}
            data={data.map((item) => ({
              key: item.id,
              serial_number: (page - 1) * limit + (data.indexOf(item) + 1),
              module_name: item.module_name,
              status: item.status,
              uploaded_by: item.uploaded_by
                ? `${item.uploaded_by.first_name || ""} ${
                    item.uploaded_by.last_name || ""
                  }`.trim()
                : "-",
              created_at: convertUTCToLocal(item.created_at, "MM/DD/YY"),
              error_message: item.error_message || "-",
              inserted_rows_count:
                item.inserted_rows_count == null
                  ? "-"
                  : item.inserted_rows_count,
              failed_rows_count:
                item.failed_rows_count == null ? "-" : item.failed_rows_count,
              original_file: item.file_key ? (
                <a
                  href={`${process.env.REACT_APP_S3_BULK_UPLOAD_BUCKET_DOMAIN}/${item.file_key}`}
                  download
                >
                  <img src={downloadIcon} alt="Download" />
                </a>
              ) : (
                "-"
              ),
              output_file: item.output_file_key && item.status.toLowerCase() !== "failed" ? (
                <a
                  href={`${process.env.REACT_APP_S3_BULK_UPLOAD_BUCKET_DOMAIN}/${item.output_file_key}`}
                  download
                >
                  <img src={downloadIcon} alt="Download" />
                </a>
              ) : (
                "-"
              ),
            }))}
            onSort={handleSort}
            noResultsTxt={
              appliedFilters.moduleName ||
              appliedFilters.status ||
              appliedFilters.uploadedBy ||
              appliedFilters.uploadDate
                ? "No uploads found for the selected filters."
                : "No data found"
            }
          />
        </div>
      </div>
      {totalRecords > 0 && (
        <div className="leads-pagination-container">
          <PaginationComponent
            totalRecords={totalRecords}
            pageSize={limit}
            currentPage={page}
            onPageChange={(event, value) => {
              setPage(value);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default TrackUploads;
